// Arrow keys move focus between options; Elm owns what is open and what is
// chosen. Moving focus is a DOM question — which element comes after this one,
// skipping the ones that cannot take it — and the answer is always inside this
// element, so it needs no ids and no ports.
//
// Selects used to get this free from native radios sharing a name. Options are
// not radios any more (a control inside role=option is nested-interactive), so
// the behaviour lives here.

const OPTION = '[role="option"]';
const FOCUSABLE = `${OPTION}:not([aria-disabled="true"])`;

class ElmListbox extends HTMLElement {
  connectedCallback() {
    this.onKeyDown = this.onKeyDown.bind(this);
    this.addEventListener("keydown", this.onKeyDown);
    this.focusInitial();
  }

  disconnectedCallback() {
    this.removeEventListener("keydown", this.onKeyDown);
  }

  own(selector) {
    return [...this.querySelectorAll(selector)].filter((node) => node.closest("elm-listbox") === this);
  }

  // The listbox only exists while the popup is open, so connecting is opening.
  focusInitial() {
    const options = this.own(FOCUSABLE);
    const selected = options.find((option) => option.getAttribute("aria-selected") === "true");

    (selected ?? options[0])?.focus();
  }

  onKeyDown(event) {
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    const options = this.own(FOCUSABLE);
    if (!options.length) return;

    let next;
    if (step) {
      const here = options.indexOf(event.target.closest(OPTION));
      next = options[(here + step + options.length) % options.length];
    } else if (event.key === "Home") {
      next = options[0];
    } else if (event.key === "End") {
      next = options[options.length - 1];
    } else {
      return;
    }

    // Otherwise the page scrolls under the open popup.
    event.preventDefault();
    next.focus();
  }
}

if (!customElements.get("elm-listbox")) {
  customElements.define("elm-listbox", ElmListbox);
}
